import type { OrderCard, OrderHandoff, OrderServiceMode } from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { Package, RefreshCw, Trash2, Truck } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { Button } from "../primitives/Button";
import { Combobox } from "../primitives/Combobox";
import type { FieldControlProps } from "../primitives/Field";
import { Input, Textarea } from "../primitives/Input";
import { Checkbox, RadioGroup } from "../primitives/Selection";
import { FormField, FormSection } from "./form/Form";
import { MoneyInput } from "./form/MoneyInput";
import {
	addExpectedReturn,
	asksHandoff,
	cardBlockedBy,
	effectiveMode,
	HANDOFFS,
	ORDER_CARDS,
	type OrderCardsValue,
	SERVICE_MODES,
	toggleCard,
} from "./orderCards";

/**
 * The New Order forms' service mode and order-type boxes (ADJ-1022-3), shared
 * by the console and the Store Portal as the pickup drawer is: each app hands
 * in what it reads differently — the pickup warehouse control and the
 * catalogue search — and the rules live in `orderCards.ts`.
 *
 * Laid out as the owner's previous software was: a card per box with its icon,
 * its title and a line saying what it means, and the box's questions opening
 * under it when ticked. A box that contradicts one already ticked says so and
 * cannot be ticked, rather than failing on submit.
 *
 * Both render inside the caller's `Form`, so each field reads its own error
 * under the wire's name (`pickupWarehouseId`, `expectedReturns`, …).
 */

const ICONS: Record<OrderCard, typeof Package> = {
	pickup: Package,
	exchange: RefreshCw,
	pickup_design: Truck,
};

export interface OrderServiceFieldsProps {
	value: OrderCardsValue;
	onChange: (next: OrderCardsValue) => void;
	t: Translate;
	disabled?: boolean;
	className?: string;
}

/** Fulfilment + delivery or delivery only, and how a delivery-only parcel reaches us. */
export function OrderServiceFields({
	value,
	onChange,
	t,
	disabled = false,
	className,
}: OrderServiceFieldsProps) {
	const forced = value.cards.includes("pickup_design");
	const mode = effectiveMode(value);
	return (
		<FormSection
			{...(className === undefined ? {} : { className })}
			title={t("orderCards.service.title")}
			description={t(forced ? "orderCards.service.designForced" : `orderCards.service.${mode}Hint`)}
		>
			<RadioGroup
				aria-label={t("orderCards.service.title")}
				horizontal
				value={mode}
				disabled={disabled || forced}
				onValueChange={(next) => {
					const serviceMode = next as OrderServiceMode;
					// A pickup is served from our stock: a delivery-only order
					// cannot keep the box (`order_types_conflict`).
					const cards =
						serviceMode === "delivery_only"
							? value.cards.filter((c) => c !== "pickup")
							: value.cards;
					onChange({ ...value, serviceMode, cards });
				}}
				options={SERVICE_MODES.map((option) => ({
					value: option,
					label: t(`orderCards.service.${option}`),
				}))}
			/>
			{asksHandoff(value) && (
				<FormField name="handoff" label={t("orderCards.handoff.label")}>
					{(control) => (
						<div {...control}>
							<RadioGroup
								aria-label={t("orderCards.handoff.label")}
								value={value.handoff}
								disabled={disabled}
								onValueChange={(next) => onChange({ ...value, handoff: next as OrderHandoff })}
								options={HANDOFFS.map((option) => ({
									value: option,
									label: t(`orderCards.handoff.${option}`),
								}))}
							/>
						</div>
					)}
				</FormField>
			)}
		</FormSection>
	);
}

/** One catalogue match the expected-items search offers. */
export interface CardSkuMatch {
	sku: string;
	name: string;
}

export interface OrderTypeCardsProps {
	value: OrderCardsValue;
	onChange: (next: OrderCardsValue) => void;
	t: Translate;
	currency: string;
	/** The pickup warehouse control: the console's directory picker, the portal's list. */
	renderWarehouse: (control: FieldControlProps & { name: string }) => ReactNode;
	/** The store's catalogue, by name or SKU, for an exchange's expected items. */
	searchSkus: (query: string, signal: AbortSignal) => Promise<CardSkuMatch[]>;
	/** An order's boxes are fixed once it exists. */
	disabled?: boolean;
	className?: string;
}

/** The three order-type boxes, combinable, each with its questions. */
export function OrderTypeCards({
	value,
	onChange,
	t,
	currency,
	renderWarehouse,
	searchSkus,
	disabled = false,
	className,
}: OrderTypeCardsProps) {
	const set = (patch: Partial<OrderCardsValue>) => onChange({ ...value, ...patch });
	return (
		<FormSection
			{...(className === undefined ? {} : { className })}
			title={t("orderCards.title")}
			description={t("orderCards.hint")}
		>
			{ORDER_CARDS.map((card) => {
				const Icon = ICONS[card];
				const ticked = value.cards.includes(card);
				const blocked = cardBlockedBy(card, value);
				return (
					<div
						key={card}
						className={`flex flex-col gap-3 rounded-md border p-3 ${
							ticked ? "border-brand bg-bg-surface" : "border-border"
						}`}
					>
						<div className="flex items-start gap-3">
							<Checkbox
								checked={ticked}
								disabled={disabled || blocked !== null}
								aria-label={t(`orderCards.card.${card}.title`)}
								onCheckedChange={(checked) => onChange(toggleCard(value, card, checked === true))}
								className="mt-0.5"
							/>
							<div className="flex min-w-0 flex-col gap-0.5">
								<p className="flex items-center gap-1.5 text-[13px] font-bold text-fg">
									<Icon aria-hidden className="size-4 text-fg-muted" />
									{t(`orderCards.card.${card}.title`)}
								</p>
								<p className="text-[12.5px] text-fg-muted">{t(`orderCards.card.${card}.body`)}</p>
								{blocked !== null && (
									<p className="text-[11.5px] text-warning-fg">
										{t(`orderCards.blocked.${blocked}`)}
									</p>
								)}
							</div>
						</div>
						{ticked && card === "pickup" && (
							<div className="flex flex-col gap-3 sm:ps-7">
								<FormField
									name="pickupWarehouseId"
									label={t("orderCards.fields.warehouse")}
									hint={t("orderCards.fields.warehouseHint")}
									required
								>
									{renderWarehouse}
								</FormField>
								<InstructionsField value={value} set={set} t={t} disabled={disabled} />
							</div>
						)}
						{ticked && card === "exchange" && (
							<div className="flex flex-col gap-3 sm:ps-7">
								<FormField
									name="exchangeNotes"
									label={t("orderCards.fields.exchangeNotes")}
									hint={t("orderCards.fields.exchangeNotesHint")}
									optional
								>
									{(control) => (
										<Textarea
											{...control}
											value={value.exchangeNotes}
											disabled={disabled}
											placeholder={t("orderCards.fields.exchangeNotesPlaceholder")}
											onChange={(event) => set({ exchangeNotes: event.target.value })}
										/>
									)}
								</FormField>
								<ExpectedReturns
									value={value}
									set={set}
									t={t}
									searchSkus={searchSkus}
									disabled={disabled}
								/>
							</div>
						)}
						{ticked && card === "pickup_design" && (
							<div className="flex flex-col gap-3 sm:ps-7">
								<FormField
									name="pickupDetails"
									label={t("orderCards.fields.pickupDetails")}
									hint={t("orderCards.fields.pickupDetailsHint")}
									required
								>
									{(control) => (
										<Textarea
											{...control}
											value={value.pickupDetails}
											disabled={disabled}
											placeholder={t("orderCards.fields.pickupDetailsPlaceholder")}
											onChange={(event) => set({ pickupDetails: event.target.value })}
										/>
									)}
								</FormField>
								<InstructionsField
									value={value}
									set={set}
									t={t}
									disabled={disabled}
									hint={t("orderCards.fields.designInstructionsHint")}
								/>
								<FormField
									name="designRefundMinor"
									label={t("orderCards.fields.refund")}
									hint={t("orderCards.fields.refundHint")}
									optional
								>
									{(control) => (
										<MoneyInput
											{...control}
											currency={currency}
											min={0}
											value={value.designRefundMinor}
											disabled={disabled}
											onChange={(next) => set({ designRefundMinor: next })}
										/>
									)}
								</FormField>
							</div>
						)}
					</div>
				);
			})}
		</FormSection>
	);
}

function InstructionsField({
	value,
	set,
	t,
	disabled,
	hint,
}: {
	value: OrderCardsValue;
	set: (patch: Partial<OrderCardsValue>) => void;
	t: Translate;
	disabled: boolean;
	hint?: string;
}) {
	return (
		<FormField
			name="pickupInstructions"
			label={t("orderCards.fields.pickupInstructions")}
			{...(hint === undefined ? {} : { hint })}
			optional
		>
			{(control) => (
				<Textarea
					{...control}
					value={value.pickupInstructions}
					disabled={disabled}
					placeholder={t("orderCards.fields.pickupInstructionsPlaceholder")}
					onChange={(event) => set({ pickupInstructions: event.target.value })}
				/>
			)}
		</FormField>
	);
}

/** An exchange's expected items: search, quantity, notes, Add; each row removable. */
function ExpectedReturns({
	value,
	set,
	t,
	searchSkus,
	disabled,
}: {
	value: OrderCardsValue;
	set: (patch: Partial<OrderCardsValue>) => void;
	t: Translate;
	searchSkus: (query: string, signal: AbortSignal) => Promise<CardSkuMatch[]>;
	disabled: boolean;
}) {
	const matches = useRef(new Map<string, CardSkuMatch>());
	const [picked, setPicked] = useState<CardSkuMatch | null>(null);
	const [qty, setQty] = useState(1);
	const [notes, setNotes] = useState("");
	const add = () => {
		if (picked === null || qty < 1) {
			return;
		}
		set({
			expectedReturns: addExpectedReturn(value.expectedReturns, {
				sku: picked.sku,
				name: picked.name,
				qty,
				notes,
			}),
		});
		setPicked(null);
		setQty(1);
		setNotes("");
	};
	return (
		<FormField
			name="expectedReturns"
			label={t("orderCards.fields.expected")}
			hint={t("orderCards.fields.expectedHint")}
			required
		>
			{(control) => (
				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
						<Combobox
							{...control}
							className="min-w-0 flex-1"
							value={picked?.sku ?? null}
							{...(picked === null ? {} : { selectedLabel: `${picked.name} · ${picked.sku}` })}
							disabled={disabled}
							placeholder={t("orderCards.fields.expectedSearch")}
							emptyLabel={t("orderCards.fields.expectedEmpty")}
							loadOptions={async (query, signal) => {
								const rows = await searchSkus(query, signal);
								matches.current = new Map(rows.map((row) => [row.sku, row]));
								return rows.map((row) => ({ value: row.sku, label: row.name, hint: row.sku }));
							}}
							onValueChange={(sku) =>
								setPicked(sku === null ? null : (matches.current.get(sku) ?? null))
							}
						/>
						<Input
							type="number"
							min={1}
							className="sm:w-20"
							aria-label={t("orderCards.fields.expectedQty")}
							value={qty === 0 ? "" : String(qty)}
							disabled={disabled}
							onChange={(event) => setQty(Number(event.target.value) || 0)}
						/>
						<Input
							className="sm:w-48"
							aria-label={t("orderCards.fields.expectedNotes")}
							placeholder={t("orderCards.fields.expectedNotes")}
							value={notes}
							maxLength={120}
							disabled={disabled}
							onChange={(event) => setNotes(event.target.value)}
						/>
						<Button
							type="button"
							variant="secondary"
							disabled={disabled || picked === null || qty < 1}
							onClick={add}
						>
							{t("orderCards.fields.expectedAdd")}
						</Button>
					</div>
					{value.expectedReturns.length > 0 && (
						<ul className="flex flex-col gap-1">
							{value.expectedReturns.map((row) => (
								<li
									key={row.rowId}
									className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[12.5px]"
								>
									<span className="min-w-0 flex-1 truncate">
										<span className="font-bold text-fg">{row.name}</span>{" "}
										<span className="font-mono text-fg-muted">{row.sku}</span>
										{row.notes !== "" && <span className="text-fg-muted"> · {row.notes}</span>}
									</span>
									<span className="tabular-nums">
										{t("orderCards.detail.qty", { qty: row.qty })}
									</span>
									<Button
										type="button"
										variant="ghost"
										disabled={disabled}
										aria-label={t("orderCards.fields.expectedRemove", { name: row.name })}
										onClick={() =>
											set({
												expectedReturns: value.expectedReturns.filter((r) => r.rowId !== row.rowId),
											})
										}
									>
										<Trash2 aria-hidden className="size-4" />
									</Button>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</FormField>
	);
}

/** What an order says about its boxes and its service, as a detail page shows it. */
export interface OrderCardsFacts {
	cards: OrderCard[];
	serviceMode: OrderServiceMode;
	handoff?: OrderHandoff | null;
	pickupWarehouseId?: string | null;
	pickupWarehouseName?: string | null;
	pickupInstructions?: string | null;
	exchangeNotes?: string | null;
	expectedReturns: { sku: string; title?: string | null; qty: number; notes?: string | null }[];
	pickupDetails?: string | null;
	designRefundMinor: number;
}

export interface OrderCardsSummaryProps {
	order: OrderCardsFacts;
	t: Translate;
	/** Formats an amount in the order's currency. */
	money: (amountMinor: number) => string;
}

/**
 * An order's service, handoff, boxes and their answers (ADJ-1022-3), for the
 * console's and the portal's order pages. A row with nothing to say is left
 * out rather than drawn as a dash.
 */
export function OrderCardsSummary({ order, t, money }: OrderCardsSummaryProps) {
	const rows: { key: string; label: string; value: string }[] = [
		{
			key: "service",
			label: t("orderCards.service.title"),
			value: t(`orderCards.service.${order.serviceMode}`),
		},
	];
	if (order.handoff !== undefined && order.handoff !== null) {
		rows.push({
			key: "handoff",
			label: t("orderCards.handoff.label"),
			value: t(`orderCards.handoff.${order.handoff}`),
		});
	}
	rows.push({
		key: "cards",
		label: t("orderCards.title"),
		value:
			order.cards.length === 0
				? t("orderCards.detail.delivery")
				: order.cards.map((card) => t(`orderCards.card.${card}.title`)).join(" · "),
	});
	const text = (key: string, label: string, value: string | null | undefined) => {
		if (value !== undefined && value !== null && value !== "") {
			rows.push({ key, label, value });
		}
	};
	text(
		"warehouse",
		t("orderCards.fields.warehouse"),
		order.pickupWarehouseName ?? order.pickupWarehouseId,
	);
	text("instructions", t("orderCards.fields.pickupInstructions"), order.pickupInstructions);
	text("exchangeNotes", t("orderCards.fields.exchangeNotes"), order.exchangeNotes);
	text("pickupDetails", t("orderCards.fields.pickupDetails"), order.pickupDetails);
	if (order.designRefundMinor > 0) {
		rows.push({
			key: "refund",
			label: t("orderCards.fields.refund"),
			value: money(order.designRefundMinor),
		});
	}
	return (
		<div className="flex flex-col gap-3">
			<dl className="grid gap-x-4 gap-y-1 text-[12.5px] sm:grid-cols-[minmax(0,10rem)_1fr]">
				{rows.map((row) => (
					<div key={row.key} className="contents">
						<dt className="text-fg-muted">{row.label}</dt>
						<dd className="whitespace-pre-line text-fg">{row.value}</dd>
					</div>
				))}
			</dl>
			{order.expectedReturns.length > 0 && (
				<div className="flex flex-col gap-1">
					<p className="text-[12.5px] font-bold text-fg">{t("orderCards.fields.expected")}</p>
					<ExpectedReturnsList rows={order.expectedReturns} t={t} />
				</div>
			)}
		</div>
	);
}

/**
 * An exchange's expected items, read-only: the order page's, and the return
 * inspection's "Items received" beside what came back.
 */
export function ExpectedReturnsList({
	rows,
	t,
}: {
	rows: OrderCardsFacts["expectedReturns"];
	t: Translate;
}) {
	return (
		<ul className="flex flex-col gap-1 text-[12.5px]">
			{rows.map((row) => (
				<li key={row.sku} className="flex gap-2">
					<span className="min-w-0 flex-1">
						{row.title ?? row.sku} <span className="font-mono text-fg-muted">{row.sku}</span>
						{row.notes !== undefined && row.notes !== null && row.notes !== "" && (
							<span className="text-fg-muted"> · {row.notes}</span>
						)}
					</span>
					<span className="tabular-nums">{t("orderCards.detail.qty", { qty: row.qty })}</span>
				</li>
			))}
		</ul>
	);
}
